class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
  }

  connectedCallback() {
    this.initialize();
  }

  initialize() {
    const addToCartModule = document.querySelector(".wt-product__add-to-cart");
    const btn = this.querySelector("button");
    const addToCart = this.dataset.addToCart === "";

    const forObserver = document.querySelectorAll(
      ".wt-product__add-to-cart, .wt-footer, .wt-product__name",
    );

    let intersected = [];

    btn.addEventListener("click", (e) => {
      if (!addToCart) {
        e.preventDefault();
      }
      addToCartModule.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        isIntersecting
          ? intersected.push(target)
          : (intersected = intersected.filter((item) => item !== target));
      });

      if (intersected.length) {
        this.classList.remove(this.activeClass);
      } else {
        this.classList.add(this.activeClass);
      }
    });

    forObserver.forEach((item) => {
      observer.observe(item);
    });
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          // WOOMN: on desktop the gallery is its own scroll area (see custom.css),
          // so read its scroll position instead of the page's when that's the case.
          const galleryScrolls =
            images.scrollHeight > images.clientHeight + 1 &&
            getComputedStyle(images).overflowY !== "visible";
          const scrolled = galleryScrolls ? images.scrollTop : window.scrollY;
          const maxHeight = galleryScrolls
            ? images.scrollHeight - images.clientHeight
            : images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));
        this.querySelector("gallery-section")?.addEventListener(
          "scroll",
          updateProgressBar.bind(this),
          { passive: true },
        );

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              const masonry = this.querySelector(".swiper-wrapper--masonry");
              if (masonry) {
                stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
              }
            });
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}

/* ============================================================
   WOOMN — Product page rating badge
   Reads the hidden rating data (rendered by product-head.liquid
   from the product's rating metafields) and injects the same
   badge used on product cards into every gallery image slide,
   pinned to the bottom-right via the .wmn-rb--pdp CSS modifier.
   Falls back to the same seeded-synthetic rating used on product
   cards when the product has no rating metafield set yet.

   Also injects a "Try On" button (bottom-left of every slide) that
   links out to the WOOMN live virtual try-on mirror.
   ============================================================ */
(function () {
  function seededRand(str) {
    var h = 5381;
    for (var i = 0; i < str.length; i++) {
      h = Math.imul(h, 31) ^ str.charCodeAt(i);
    }
    return (h >>> 0) / 4294967295;
  }

  function initPdpRatingBadge() {
    var gallery = document.querySelector("[data-gallery]");
    if (!gallery) return;

    var dataEl = document.getElementById("wmn-pdp-rating-data");
    var score = dataEl ? dataEl.getAttribute("data-score") : "";
    var count = dataEl ? dataEl.getAttribute("data-count") : "";
    var source = "metafield";

    if (!score) {
      var seed = window.location.pathname;
      score = (3.5 + seededRand(seed) * 1.5).toFixed(1);
      count = String(Math.floor(25 + seededRand(seed + "c") * 21));
      source = "synthetic";
    }
    if (!count) count = "0";

    /* Anchor to every image slide (not the gallery wrapper) so the badge
       stays clipped inside each picture instead of overlapping the
       pagination dots/thumbnails below it, and shows on every slide as
       the shopper swipes through the gallery. Swiper slides are already
       position:relative and sized to the image's aspect ratio. */
    var slides = gallery.querySelectorAll("li[data-swiper-slide], .swiper-slide");
    slides.forEach(function (slide) {
      if (slide.querySelector(".wmn-rb--pdp")) return;

      var badge = document.createElement("div");
      badge.className = "wmn-rb wmn-rb--pdp";
      badge.setAttribute("data-rb-source", source);
      badge.innerHTML =
        '<span class="wmn-rb__score">' + score + "</span>" +
        '<span class="wmn-rb__star">★</span>' +
        '<span class="wmn-rb__pipe">|</span>' +
        '<span class="wmn-rb__count">' + count + "</span>";

      slide.appendChild(badge);
    });
  }

  var WMN_TRYON_URL = "https://tryon.shopwoomn.com";

  function getWmnProductHandle() {
    var match = window.location.pathname.match(/\/products\/([^/?]+)/);
    return match ? match[1] : "";
  }

  function initPdpTryOnButton() {
    var gallery = document.querySelector("[data-gallery]");
    if (!gallery) return;

    var handle = getWmnProductHandle();
    var tryOnHref = WMN_TRYON_URL + "/?product=" + encodeURIComponent(handle);

    var slides = gallery.querySelectorAll("li[data-swiper-slide], .swiper-slide");
    slides.forEach(function (slide) {
      if (slide.querySelector(".wmn-tryon")) return;

      var btn = document.createElement("a");
      btn.href = tryOnHref;
      btn.className = "wmn-tryon";
      btn.style.textDecoration = "none";
      btn.setAttribute("aria-label", "Try on virtually");
      btn.innerHTML =
        '<span class="wmn-tryon__dots">' +
          "<span></span><span></span><span></span><span></span>" +
        "</span>" +
        '<span class="wmn-tryon__label">TRY ON</span>';

      // Click handling is now taken over by the WOOMN Live Mirror overlay
      // interceptor (added in layout/theme.liquid), which catches any
      // click on a link pointing at WMN_TRYON_URL and opens it as a
      // full-screen overlay instead of navigating/opening a new tab.

      slide.appendChild(btn);

      // Trigger the pop-in animation shortly after it's added to the DOM
      window.setTimeout(function () {
        btn.classList.add("wmn-tryon--pop");
      }, 1000);
    });
  }

  function initPdpGalleryExtras() {
    initPdpRatingBadge();
    initPdpTryOnButton();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPdpGalleryExtras);
  } else {
    initPdpGalleryExtras();
  }

  document.addEventListener("shopify:section:load", initPdpGalleryExtras);

  /* Re-run if the gallery swaps slides (e.g. variant/color change) */
  var galleryEl = document.querySelector("[data-gallery]");
  if (galleryEl && window.MutationObserver) {
    var debounce = null;
    new MutationObserver(function () {
      clearTimeout(debounce);
      debounce = setTimeout(initPdpGalleryExtras, 50);
    }).observe(galleryEl, { childList: true, subtree: true });
  }
})();
